import React, { useCallback, useMemo } from "react";
import Button from "domains/ui/components/Button";
import useResponsiveBreakpoints from "domains/ui/hooks/useResponsiveBreakpoints";
import { GetModelsDescriptionByModelIdApiResponse } from "infra/api/generated/api";
import MarkdownToJSX from "markdown-to-jsx";

import { Text } from "@chakra-ui/react";

import Asset from "./CustomRenderers/Asset";
import Model from "./CustomRenderers/Model";
import { overrides } from "./constants";
import { transformXMLTags } from "./utils";

type ModelsDescriptionType =
  GetModelsDescriptionByModelIdApiResponse["description"];

interface CustomTagProps {
  type: string;
  id: string;
}

export interface MarkdownViewerProps extends ModelsDescriptionType {
  emptyState?: string;
  isTruncated?: boolean;
  onSeeMore?: () => void;
  isOnLoraCatalog?: boolean;
}

const responsiveMaxCharBreakpoints = {
  values: {
    1_280: 500,
    992: 150,
    768: 75,
    480: 50,
    360: 30,
  },
  default: 512,
};

const MarkdownViewer = ({
  value,
  models,
  assets,
  isTruncated,
  onSeeMore,
  isOnLoraCatalog,
  emptyState = "No description provided",
}: MarkdownViewerProps) => {
  const maxCharBreakpoint = useResponsiveBreakpoints(
    responsiveMaxCharBreakpoints
  );
  const isEmptyContent = !value || value.trim() === "";

  const transformedContent = useMemo(() => {
    if (isEmptyContent) {
      return { content: emptyState, isTruncated: false };
    }
    let newContent = transformXMLTags(value);
    // Normalize excessive newlines to a maximum of two
    newContent = newContent
      .replaceAll(/<br\s*\/?>/gi, "\n")
      .replaceAll(/(\s*\n\s*)+/g, " \n \n ");

    const effectiveLength = newContent.split("").reduce((acc, char) => {
      if (char === "\n") {
        return acc + 20; // Assuming a new line weight of 20 characters
      }
      return acc + 1;
    }, 0);

    if (!isTruncated) {
      return {
        content: newContent.replace(/\n/g, "<br>"),
        isTruncated: false,
      };
    }

    let truncatedContent = "";
    let currentLength = 0;
    let lineLength = 0;
    const maxLineLength = 80; // Maximum characters in a line before forcing a new line

    for (const char of newContent) {
      if (char === "\n" || lineLength >= maxLineLength) {
        if (currentLength + 20 > maxCharBreakpoint) break;
        currentLength += 20;
        lineLength = 0;
      } else {
        if (currentLength + 1 > maxCharBreakpoint) break;
        currentLength += 1;
        lineLength += 1;
      }
      truncatedContent += char;
    }

    truncatedContent = truncatedContent.replace(/\n/g, "<br>");

    // Add ellipsis if truncated
    if (truncatedContent.length < newContent.length) {
      truncatedContent = truncatedContent.trimEnd() + "... ";
    }

    return {
      content: truncatedContent,
      isTruncated: effectiveLength > maxCharBreakpoint,
    };
  }, [isEmptyContent, value, isTruncated, emptyState, maxCharBreakpoint]);

  const CustomTag = useCallback(
    ({ type, id }: CustomTagProps) => {
      const renderCustomScComponent = (): JSX.Element => {
        switch (type) {
          case "model":
            return (
              <Model
                data={models?.find((model) => model.id === id)}
                withAddToCompositionButton={isOnLoraCatalog}
              />
            );
          case "asset":
            return <Asset data={assets?.find((asset) => asset.id === id)} />;
          default:
            return <></>;
        }
      };

      return renderCustomScComponent();
    },
    [models, isOnLoraCatalog, assets]
  );

  const overrideMemoized = useMemo(() => {
    return { ...overrides, CustomTag };
  }, [CustomTag]);

  const hasViewMoreButton = onSeeMore && transformedContent.isTruncated;

  return (
    <Text flexWrap={"wrap"} color={"textSecondary"}>
      <MarkdownToJSX options={{ overrides: overrideMemoized }}>
        {transformedContent.content}
      </MarkdownToJSX>
      {hasViewMoreButton && (
        <Button
          size="xs"
          variant="ghost"
          textDecor="underline"
          onClick={onSeeMore}
          px={0}
        >
          View more
        </Button>
      )}
    </Text>
  );
};

export default MarkdownViewer;
